import React, { useState, useEffect } from "react";
import notificationStore from "../../stores/Notification/notificationStore";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Alert,
  Divider,
  Box,
} from "@mui/material";
import { DataPrivacyPolicyStatus } from "../../types/DataPrivacyPolicyStatus";
import { usePatient } from "../../stores/Patients/patient.queries";
import { useCreateOrUpdatePatient } from "../../stores/Patients/patient.mutations";

// Datenschutz Component
export const PatientDatenschutzViewer = () => {
  const PatientStore = useStore(PatientsContext);
  const { selectedPatientId: patientId } = PatientStore;

  // Fetch patient data using React Query
  const { data: patientQueryData } = usePatient(patientId!, !!patientId);
  const createOrUpdatePatientMutation = useCreateOrUpdatePatient();

  // State variables
  const [dataPrivacyPolicyStatus, setDataPrivacyPolicyStatus] = useState<string>(
    DataPrivacyPolicyStatus.NOT_INFORMED
  );
  const [originalStatus, setOriginalStatus] = useState<string>(
    DataPrivacyPolicyStatus.NOT_INFORMED
  );
  const [loading, setLoading] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);

  // Sync patient data from React Query
  useEffect(() => {
    if (patientQueryData) {
      setPatientData(patientQueryData);
      if (patientQueryData.dataPrivacyPolicyStatus) {
        setDataPrivacyPolicyStatus(patientQueryData.dataPrivacyPolicyStatus);
        setOriginalStatus(patientQueryData.dataPrivacyPolicyStatus);
      } else {
        setDataPrivacyPolicyStatus(DataPrivacyPolicyStatus.NOT_INFORMED);
        setOriginalStatus(DataPrivacyPolicyStatus.NOT_INFORMED);
      }
    }
  }, [patientQueryData]);

  // Handle status change
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = event.target.value as DataPrivacyPolicyStatus;
    setDataPrivacyPolicyStatus(newStatus);
    setHasChanges(newStatus !== originalStatus);
  };

  // Handle save
  const handleSave = async () => {
    setLoading(true);
    try {
      if (patientData) {
        // Stelle sicher, dass der Wert immer ein gültiger String ist
        const updatedPatient = {
          ...patientData,
          dataPrivacyPolicyStatus: dataPrivacyPolicyStatus || DataPrivacyPolicyStatus.NOT_INFORMED,
        };

        // console.log("Speichere Datenschutzstatus:", updatedPatient.dataPrivacyPolicyStatus);

        const result = await createOrUpdatePatientMutation.mutateAsync(updatedPatient);

        if (true) {
          // Aktualisiere den globalen Patientendaten-Refresh-Flag
          // setPatientDataRefreshFlag(!patientDataRefreshFlag);

          // Aktualisiere lokalen Refresh-Flag
          setRefreshFlag(!refreshFlag);

          setOriginalStatus(dataPrivacyPolicyStatus);
          setHasChanges(false);

          notificationStore.showMessage("Datenschutzstatus erfolgreich aktualisiert", "success");
        } else {
          throw new Error("Fehler beim Speichern der Patientendaten");
        }
      }
    } catch (error) {
      notificationStore.showMessage(
        "Fehler beim Aktualisieren des Datenschutzstatus",
        "error",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // Define status text color based on status
  const getStatusColor = (status: string) => {
    switch (status) {
      case DataPrivacyPolicyStatus.NOT_INFORMED:
        return "error.main";
      case DataPrivacyPolicyStatus.INFORMED_VIA_POSTER:
        return "success.main";
      case DataPrivacyPolicyStatus.SIGNED:
        return "success.main";
      default:
        return "text.primary";
    }
  };

  return (
    <Card>
      <CardHeader
        title="Datenschutzerklärung"
        subheader={loading ? "Wird geladen..." : "Status der Datenschutzerklärung"}
      />
      <Divider />
      <CardContent>
        {originalStatus === DataPrivacyPolicyStatus.NOT_INFORMED && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Der Patient wurde noch nicht über die Datenschutzerklärung informiert!
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Aktueller Status:
          <Box
            component="span"
            sx={{ fontWeight: "bold", color: getStatusColor(originalStatus), ml: 1 }}
          >
            {originalStatus === DataPrivacyPolicyStatus.NOT_INFORMED && "Nicht informiert"}
            {originalStatus === DataPrivacyPolicyStatus.INFORMED_VIA_POSTER &&
              "Informiert durch Hinweis auf Aushang/Website"}
            {originalStatus === DataPrivacyPolicyStatus.SIGNED &&
              "Datenschutzerklärung unterschrieben"}
          </Box>
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Datenschutzstatus ändern:
        </Typography>

        <RadioGroup
          name="datenschutzStatus"
          value={dataPrivacyPolicyStatus}
          onChange={handleStatusChange}
        >
          <FormControlLabel
            value={DataPrivacyPolicyStatus.NOT_INFORMED}
            control={<Radio />}
            label="Nicht informiert"
          />
          <FormControlLabel
            value={DataPrivacyPolicyStatus.INFORMED_VIA_POSTER}
            control={<Radio />}
            label="Informiert durch Hinweis auf Aushang/Website"
          />
          <FormControlLabel
            value={DataPrivacyPolicyStatus.SIGNED}
            control={<Radio />}
            label="Datenschutzerklärung unterschrieben"
          />
        </RadioGroup>

        <Button
          variant="contained"
          color="primary"
          disabled={loading || !hasChanges}
          onClick={handleSave}
          sx={{ mt: 2 }}
        >
          {loading ? "Wird gespeichert..." : "Speichern"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PatientDatenschutzViewer;
